import { useEffect } from "react";

const SITE_URL = "https://nirvanacomfort.com";
const SITE_NAME = "NirvanaComfort";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data, id) {
  const scriptId = `jsonld-${id}`;
  let el = document.getElementById(scriptId);
  if (!el) {
    el = document.createElement("script");
    el.id = scriptId;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => el.remove();
}

export function SEO({ title, description, path = "", image, type = "website", noindex = false }) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Premium Mattresses, Beds & Comfort Furniture`;
  const desc = description || "Shop premium mattresses, beds, recliners & comfort furniture. Engineered for better sleep and living. Free delivery across India.";
  const url = `${SITE_URL}${path}`;
  const img = image || DEFAULT_IMAGE;

  useEffect(() => {
    document.title = fullTitle;
    setMeta("description", desc);
    setLink("canonical", url);
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", img, "property");
    setMeta("og:type", type, "property");
    setMeta("og:site_name", SITE_NAME, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", desc);
    setMeta("twitter:image", img);
    if (noindex) {
      setMeta("robots", "noindex,nofollow");
    } else {
      const el = document.querySelector('meta[name="robots"]');
      if (el) el.remove();
    }
  }, [fullTitle, desc, url, img, type, noindex]);

  return null;
}

export function ProductSEO({ product, reviewStats }) {
  const title = product?.seo_title || product?.title || product?.name || "";
  const desc = product?.seo_description || product?.short_description || product?.description?.substring(0, 160) || "";
  const image = product?.images?.[0] || product?.image_url || product?.media?.[0]?.url || "";
  const price = product?.selling_price || product?.price || product?.base_price || product?.min_price || 0;
  const slug = product?.slug || "";

  useEffect(() => {
    if (!product) return;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: title,
      description: desc,
      image: image ? [image] : [],
      url: `${SITE_URL}/product/${slug}`,
      brand: { "@type": "Brand", name: product.brand || "NirvanaComfort" },
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: price,
        availability: product.in_stock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        seller: { "@type": "Organization", name: "NirvanaComfort" },
      },
    };
    if ((reviewStats?.total || product.review_stats?.total) > 0) {
      const stats = reviewStats || product.review_stats;
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: stats.average,
        reviewCount: stats.total,
        bestRating: 5,
        worstRating: 1,
      };
    }
    return setJsonLd(schema, `product-${slug}`);
  }, [product, title, desc, image, price, slug]);

  if (!product) return null;
  return <SEO title={title} description={desc} path={`/product/${slug}`} image={image} type="product" />;
}

export function CategorySEO({ category }) {
  const title = category?.seo_title || category?.name || "";
  const desc = category?.seo_description || (category ? `Shop ${category.name} at NirvanaComfort. Premium quality with free delivery across India.` : "");
  const slug = category?.slug || "";

  useEffect(() => {
    if (!category) return;
    return setJsonLd({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description: desc,
      url: `${SITE_URL}/category/${slug}`,
    }, `category-${slug}`);
  }, [category, title, desc, slug]);

  if (!category) return null;
  return <SEO title={title} description={desc} path={`/category/${slug}`} />;
}

export function OrganizationSchema() {
  useEffect(() => {
    return setJsonLd({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "NirvanaComfort",
      url: SITE_URL,
      logo: `${SITE_URL}/favicon.png`,
      description: "Premium mattresses, beds, recliners & comfort furniture. Engineered for better sleep and living.",
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+91-9811742827",
        contactType: "customer service",
        areaServed: "IN",
        availableLanguage: ["English", "Hindi"],
      },
    }, "organization");
  }, []);

  return null;
}

export function BreadcrumbSchema({ items }) {
  useEffect(() => {
    if (!items?.length) return;
    return setJsonLd({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        item: item.url ? `${SITE_URL}${item.url}` : undefined,
      })),
    }, "breadcrumbs");
  }, [items]);

  return null;
}

export function FAQPageSchema({ categories }) {
  useEffect(() => {
    if (!categories?.length) return;
    const mainEntity = categories.flatMap(cat =>
      (cat.items || []).map(faq => ({
        "@type": "Question",
        name: faq.q,
        acceptedAnswer: { "@type": "Answer", text: faq.a },
      }))
    );
    return setJsonLd({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity,
    }, "faq-page");
  }, [categories]);

  return null;
}
